import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutGrid, PenTool, Palette, PersonStanding, SwatchBook, Images, FolderOpen, LogOut, Layers, Brush, Users, FileText, Calculator, ShoppingCart, CalendarDays, Puzzle, Package, Droplets, Factory, FileSpreadsheet, FlaskConical, CheckCircle, Shield, Barcode, HardDrive, BarChart3, GitBranch, Plug, Bot, ChevronDown, ChevronRight, Paintbrush, Ruler, Scissors, TrendingUp, Award, Building2
} from "lucide-react";
import { NotificationBell } from "./NotificationBell";
import { useState } from "react";

/**
 * The sidebar follows the SOW's product development lifecycle, in the order the work is done:
 * Plan → Create → Develop → Build → Source → Quality → Manage → System.
 *
 * It used to be a 13-item top-level list plus a collapsed "PLM" submenu holding 22 more, which
 * split the lifecycle across two places: Costing sat top-level while RFQs and Suppliers were
 * nested, so sourcing a garment meant crossing the divide twice. Grouping is now by phase, every
 * destination is top-level, and the group headings are labels rather than menus — the exception
 * is System, which is collapsed because it is configuration rather than a stage of the work.
 *
 * Routes and page components are unchanged; only order and grouping moved.
 */
const NAV_GROUPS = [
  {
    label: "Plan",
    items: [
      { to: "/dashboard", label: "Dashboard", icon: LayoutGrid },
      { to: "/plm/collection-planning", label: "Collection Planning", icon: FolderOpen },
      { to: "/plm/line-planning", label: "Line Planning", icon: BarChart3 },
      { to: "/plm/ta-calendar", label: "T&A Calendar", icon: CalendarDays },
    ],
  },
  {
    label: "Create",
    items: [
      { to: "/studio/design", label: "Design Studio", icon: Scissors },
      { to: "/studio/sketch", label: "Sketch to Render", icon: PenTool },
      { to: "/studio/materials", label: "Apply Materials", icon: Palette },
      { to: "/studio/bulk", label: "Bulk Colorways", icon: Layers },
      { to: "/studio/tryon", label: "Model Try-On", icon: PersonStanding },
    ],
  },
  {
    label: "Develop",
    items: [
      { to: "/plm/components", label: "Components", icon: Puzzle },
      { to: "/plm/materials", label: "Materials Master", icon: Package },
      { to: "/plm/colours", label: "Colours & Lab Dips", icon: Droplets },
      { to: "/plm/artworks", label: "Artworks", icon: Paintbrush },
      { to: "/plm/size-charts", label: "Size Charts", icon: Ruler },
      { to: "/styles", label: "Brand Styles", icon: Brush },
    ],
  },
  {
    label: "Build",
    items: [
      { to: "/techpacks", label: "Techpacks", icon: FileText },
      { to: "/plm/pattern-engineering", label: "Pattern Engineering", icon: Scissors },
      { to: "/designs", label: "Designs", icon: Images },
    ],
  },
  {
    label: "Source",
    items: [
      // SOW 10.1 — the group's legal entities. The entity master lives on the Compliance page's
      // Entities tab; without this the feature was complete and unreachable, and a cost sheet
      // offered two empty entity dropdowns with no route to filling them.
      { to: "/plm/compliance?tab=entities", label: "Legal Entities", icon: Building2 },
      { to: "/plm/suppliers", label: "Suppliers", icon: Factory },
      { to: "/plm/supplier-evaluation", label: "Supplier Eval", icon: Award },
      { to: "/plm/rfqs", label: "RFQs", icon: FileSpreadsheet },
      { to: "/costing", label: "Costing", icon: Calculator },
      { to: "/purchase-orders", label: "Purchase Orders", icon: ShoppingCart },
      { to: "/plm/samples", label: "Samples", icon: FlaskConical },
    ],
  },
  {
    label: "Quality",
    items: [
      { to: "/plm/quality", label: "Quality Testing", icon: CheckCircle },
      { to: "/plm/compliance", label: "Compliance", icon: Shield },
      { to: "/plm/skus", label: "SKU Management", icon: Barcode },
    ],
  },
  {
    label: "Manage",
    items: [
      { to: "/plm/assets", label: "Digital Assets", icon: HardDrive },
      { to: "/collections", label: "Collections", icon: FolderOpen },
      { to: "/plm/reports", label: "Reports", icon: BarChart3 },
      { to: "/plm/workflows", label: "Workflows", icon: GitBranch },
      // Team stays admin-only, as it was when it was appended to the old top-level list.
      { to: "/team", label: "Team", icon: Users, adminOnly: true },
    ],
  },
  {
    label: "System",
    collapsed: true,
    items: [
      { to: "/ai-governance", label: "AI Governance", icon: Bot },
      { to: "/plm/cost-intelligence", label: "Cost Intelligence", icon: TrendingUp },
      { to: "/integrations", label: "Integrations", icon: Plug },
      // Kept alongside Materials Master, not merged: /library is the design studio's swatch
      // picker (Library) and /plm/materials is the governed material master
      // (MaterialManagement) with suppliers, pricing tiers and certifications. Different
      // components, different jobs — removing either would lose a page.
      { to: "/library", label: "Materials Library", icon: SwatchBook },
    ],
  },
];

const testId = (label) => `nav-${label.toLowerCase().replace(/\s+/g, "-")}`;

function NavItem({ item }) {
  const location = useLocation();
  // NavLink matches on pathname only, so two entries sharing a path (Legal Entities and
  // Compliance both live at /plm/compliance) would BOTH highlight. Where an entry names a
  // query string, the query has to match too — and a plain entry stops matching when one is set.
  const [path, query] = item.to.split("?");
  const active = location.pathname === path
    && (query ? location.search.replace(/^\?/, "") === query : !location.search.includes("tab="));

  return (
    <NavLink
      to={item.to}
      data-testid={testId(item.label)}
      className={() =>
        `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
          active ? "bg-zinc-950 text-white font-medium" : "text-zinc-600 hover:bg-zinc-100"
        }`}
    >
      <item.icon size={16} /> {item.label}
    </NavLink>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const [systemOpen, setSystemOpen] = useState(false);
  const isAdmin = user?.role === "admin";
  const visible = (items) => items.filter((i) => !i.adminOnly || isAdmin);

  return (
    <div className="min-h-screen bg-white flex">
      <aside className="w-60 border-r border-zinc-200 flex flex-col fixed inset-y-0 bg-white z-40">
        <div className="h-16 flex items-center justify-between px-6 border-b border-zinc-100">
          <Link to="/dashboard" className="font-heading font-bold tracking-tight" data-testid="shell-logo">
            LUMINA<span className="text-zinc-400">.AI</span>
          </Link>
          <NotificationBell />
        </div>
        <nav className="flex-1 py-4 px-3 overflow-y-auto">
          {NAV_GROUPS.map((group) => {
            const items = visible(group.items);
            if (items.length === 0) return null;

            if (group.collapsed) {
              return (
                <div key={group.label} className="pt-3 mt-3 border-t border-zinc-100">
                  <button
                    onClick={() => setSystemOpen(!systemOpen)}
                    data-testid={`nav-group-${group.label.toLowerCase()}`}
                    aria-expanded={systemOpen}
                    className="w-full flex items-center px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-400 hover:text-zinc-600 transition-colors"
                  >
                    {group.label}
                    {systemOpen ? <ChevronDown size={12} className="ml-auto" /> : <ChevronRight size={12} className="ml-auto" />}
                  </button>
                  {systemOpen && (
                    <div className="mt-1 space-y-0.5">
                      {items.map((item) => <NavItem key={item.to} item={item} />)}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <div key={group.label} className="mb-3">
                <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-400">
                  {group.label}
                </p>
                <div className="space-y-0.5">
                  {items.map((item) => <NavItem key={item.to} item={item} />)}
                </div>
              </div>
            );
          })}
        </nav>
        <div className="border-t border-zinc-100 p-4">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-sm font-medium truncate" data-testid="shell-user-name">{user?.name}</p>
              <p className="text-xs text-zinc-400 truncate">{user?.email}</p>
            </div>
            <button data-testid="logout-button" onClick={logout} title="Log out"
              className="p-2 rounded-md hover:bg-zinc-100 text-zinc-500 transition-colors">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>
      <main className="flex-1 ml-60 min-h-screen bg-zinc-50/50">
        <Outlet />
      </main>
    </div>
  );
}
